import { Box, Button, Heading, HStack, SimpleGrid, Stack, Text, VStack } from '@vesyl/ui/chakra'
import { CarrierLogo } from '@vesyl/ui/vesyl/logo-carrier'
import { PlatformLogo } from '@vesyl/ui/vesyl/logo-platform'
import { VESYLLogo } from '@vesyl/ui/vesyl/logo'
import { useColorMode } from '@vesyl/ui'
import { useState } from 'react'

// Unique carriers only (no duplicates that share the same logo)
const carriers = [
  { code: 'UPS', aliases: ['UPSDAP', 'UPSMAILINNOVATIONS', 'UPSSUREPOST'] },
  { code: 'USPS', aliases: [] },
  { code: 'FEDEX', aliases: ['FEDEXSMARTPOST'] },
  { code: 'OSM', aliases: ['OSMWORLDWIDE'] },
  { code: 'DHLEXPRESS', aliases: [] },
  { code: 'CDL', aliases: ['COLUMBUSLASTMILE'] },
  { code: 'EPG', aliases: ['RRDONNELLEY'] },
  { code: 'LASERSHIP', aliases: [] },
  { code: 'UDS', aliases: ['UNITEDDELIVERYSERVICE'] },
  { code: 'VEHO', aliases: [] },
  { code: 'AXLEHIRE', aliases: [] },
  { code: 'PASSPORTGLOBAL', aliases: [] },
  { code: 'UNIUNI', aliases: [] },
  { code: 'PUROLATOR', aliases: [] },
  { code: 'SFEXPRESS', aliases: [] },
  { code: 'CANADAPOST', aliases: [] },
  { code: 'WIZMO', aliases: [] },
  { code: 'VESYLSELECT', aliases: [] },
]

const platforms = [
  { code: 'SHOPIFY', aliases: [] },
  { code: 'AMAZON', aliases: ['AMAZON2'] },
  { code: 'EBAY', aliases: ['EBAY2'] },
  { code: 'ETSY', aliases: [] },
  { code: 'WOOCOMMERCE', aliases: [] },
  { code: 'EXIGO', aliases: [] },
  { code: 'SHIPSTATION', aliases: [] },
]

const sizeOptions = [
  { label: 'Default (w="4")', props: {} },
  { label: 'Small (w="3")', props: { w: '3' as const } },
  { label: 'Medium (w="5")', props: { w: '5' as const } },
  { label: 'Large (w="8")', props: { w: '8' as const } },
  { label: 'XL (w="12")', props: { w: '12' as const } },
]

export default function LogosPage() {
  const { colorMode, toggleColorMode } = useColorMode()
  const [selectedSize, setSelectedSize] = useState(0)

  const currentSize = sizeOptions[selectedSize]

  return (
    <Box p="8" maxW="1400px" mx="auto">
      <VStack align="stretch" gap="8">
        {/* Header */}
        <HStack justify="space-between" wrap="wrap" gap="4">
          <Heading>Logos</Heading>
          <Button size="sm" variant="outline" onClick={toggleColorMode}>
            {colorMode === 'light' ? '🌙 Dark' : '☀️ Light'}
          </Button>
        </HStack>

        {/* Size Selector */}
        <Stack gap="3">
          <Text fontWeight="medium">Size:</Text>
          <HStack gap="2" wrap="wrap">
            {sizeOptions.map((option, index) => (
              <Button
                key={index}
                size="sm"
                variant={selectedSize === index ? 'solid' : 'outline'}
                onClick={() => setSelectedSize(index)}
              >
                {option.label}
              </Button>
            ))}
          </HStack>
        </Stack>

        {/* VESYL Logos */}
        <Stack gap="4" pt="4" borderTopWidth="1px">
          <Heading size="lg">VESYL</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} gap="4">
            <Box p="4" borderWidth="1px" borderRadius="md">
              <VStack gap="2">
                <VESYLLogo variant="full" h="8" w="auto" />
                <Text fontSize="sm" fontWeight="medium">
                  Full Logo
                </Text>
              </VStack>
            </Box>
            <Box p="4" borderWidth="1px" borderRadius="md">
              <VStack gap="2">
                <VESYLLogo variant="logomark" {...currentSize.props} />
                <Text fontSize="sm" fontWeight="medium">
                  Logomark
                </Text>
              </VStack>
            </Box>
            <Box p="4" borderWidth="1px" borderRadius="md">
              <VStack gap="2">
                <VESYLLogo variant="full" h="8" w="auto" monochrome />
                <Text fontSize="sm" fontWeight="medium">
                  Monochrome
                </Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Stack>

        {/* Platform Logos */}
        <Stack gap="4" pt="4" borderTopWidth="1px">
          <Heading size="lg">Platforms</Heading>
          <Text fontSize="sm" color="fg.muted">
            E-commerce platforms and integrations
          </Text>
          <SimpleGrid columns={{ base: 2, md: 4, lg: 7 }} gap="4">
            {platforms.map(({ code, aliases }) => (
              <Box key={code} p="4" borderWidth="1px" borderRadius="md">
                <VStack gap="2">
                  <PlatformLogo platform={code} {...currentSize.props} />
                  <VStack gap="0.5" w="full">
                    <Text fontSize="sm" fontWeight="medium" textAlign="center">
                      {code}
                    </Text>
                    {aliases.length > 0 && (
                      <Text fontSize="2xs" color="fg.muted" textAlign="center">
                        Also: {aliases.join(', ')}
                      </Text>
                    )}
                  </VStack>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Stack>

        {/* Carrier Logos */}
        <Stack gap="4" pt="4" borderTopWidth="1px">
          <Heading size="lg">Carriers</Heading>
          <Text fontSize="sm" color="fg.muted">
            Shipping carriers
          </Text>
          <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} gap="4">
            {carriers.map(({ code, aliases }) => (
              <Box key={code} p="4" borderWidth="1px" borderRadius="md">
                <VStack gap="2">
                  <CarrierLogo carrier={code} {...currentSize.props} />
                  <VStack gap="0.5" w="full">
                    <Text fontSize="sm" fontWeight="medium" textAlign="center">
                      {code}
                    </Text>
                    {aliases.length > 0 && (
                      <Text fontSize="2xs" color="fg.muted" textAlign="center" lineClamp={2}>
                        Also: {aliases.join(', ')}
                      </Text>
                    )}
                  </VStack>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Stack>

        {/* Usage Examples */}
        <Stack gap="4" pt="4" borderTopWidth="1px">
          <Heading size="lg">Usage</Heading>
          <Box
            p="4"
            borderWidth="1px"
            borderRadius="md"
            bg="bg.subtle"
            fontFamily="mono"
            fontSize="sm"
          >
            <Stack gap="2">
              <Text fontWeight="medium" fontFamily="body">
                Carrier Logos:
              </Text>
              <Text>{"import { CarrierLogo } from '@vesyl/ui/vesyl/logo-carrier'"}</Text>
              <Text>{'<CarrierLogo carrier="UPS" />'}</Text>
              <Text>{'<CarrierLogo carrier="UPS" w="6" />'}</Text>
            </Stack>
            <Stack gap="2" mt="4">
              <Text fontWeight="medium" fontFamily="body">
                Platform Logos:
              </Text>
              <Text>{"import { PlatformLogo } from '@vesyl/ui/vesyl/logo-platform'"}</Text>
              <Text>{'<PlatformLogo platform="SHOPIFY" />'}</Text>
              <Text>{'<PlatformLogo platform="SHOPIFY" w="6" />'}</Text>
            </Stack>
            <Stack gap="2" mt="4">
              <Text fontWeight="medium" fontFamily="body">
                VESYL Logo:
              </Text>
              <Text>{"import { VESYLLogo } from '@vesyl/ui/vesyl/logo'"}</Text>
              <Text>{'<VESYLLogo variant="full" />'}</Text>
              <Text>{'<VESYLLogo variant="logomark" w="8" />'}</Text>
              <Text>{'<VESYLLogo variant="full" monochrome />'}</Text>
            </Stack>
          </Box>
        </Stack>
      </VStack>
    </Box>
  )
}
